/**
 * Dialog width scale — four steps, chosen **by role**.
 *
 * Before #744, the application carried 21 different widths spread over 92 dialogs
 * (360, 400, 420, 460, 480, 500, 520, 540, 560, 600, 620, 640, 680, 700, 720, 760,
 * 960, 1100…): two twin forms opened 40 px apart without any decision having wanted
 * it. The values reduce to four steps, and this is where they live — a
 * `:max-width="600"` written by hand in a template is a fifth step that starts the
 * dispersion again.
 *
 * **How to choose**: look at what the dialog contains, never at how many pixels "it
 * looks like it needs". In order, the first step that accommodates the content wins.
 *
 * @example
 * ```vue
 * <script setup lang="ts">
 * import { DIALOG_WIDTH } from '@/utils/dialog-width';
 * </script>
 *
 * <template>
 *   <BaseDialog v-model="open" title="Modifier le stage" :max-width="DIALOG_WIDTH.standard">
 * </template>
 * ```
 */
export const DIALOG_WIDTH = {
  /**
   * **Confirmation, or a single field.** A closed question ("Confirmez-vous la
   * suppression ?"), a single choice from a short list, an isolated field with its
   * button. The content fits in a narrow column and widening it would only move the
   * text away from its button.
   */
  compact: 400,

  /**
   * **Ordinary form** — the default step, and the most frequent. Several fields stacked
   * in one column: a name, some dates, two or three dropdowns, a text area. To be chosen
   * as soon as a `v-autocomplete` has to show long labels (a module name, a company name)
   * without truncating them.
   */
  standard: 520,

  /**
   * **Wizard, or two columns.** A stepped journey, a form whose fields are spread across
   * side-by-side `v-col`s, an editable list with its per-row actions, a preview next to
   * its input. The content needs *internal* width to be readable, not just room.
   */
  large: 720,

  /**
   * **Data screen.** The dialog is no longer a form but a work surface: a multi-column
   * table, a grid, a whole annotated questionnaire. Rare, and deliberately so: if the
   * content requires this width regularly, what it needs is a page, not a dialog.
   */
  wide: 1100,
} as const;

/** The four admitted values — to type a prop that relays a width. */
export type DialogWidth = (typeof DIALOG_WIDTH)[keyof typeof DIALOG_WIDTH];
